// src/pages/Riders.js
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { listenToRiders, listenToOrders, updateRider, deleteRider } from '../services/firebase';
import toast from 'react-hot-toast';
import { FEATURES } from '../config/features';
import DateFilterBar from '../components/DateFilterBar';
import { todayKey } from '../utils/dates';
import { riderEarnings } from '../utils/orders';

const partnerLabel = FEATURES.RIDES_ENABLED ? 'Rider' : 'Delivery Partner';

export default function Riders() {
  const [riders, setRiders] = useState([]);
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [selectedDay, setSelectedDay] = useState(todayKey());
  const filterInitialized = useRef(false);

  useEffect(() => {
    const unsubOrders = listenToOrders(setOrders);
    const unsubRiders = listenToRiders((data) => {
      setRiders(data);
      setLoadError(null);
      if (!filterInitialized.current) {
        filterInitialized.current = true;
        if (data.some((r) => !r.isApproved && !r.isBlocked)) setFilter('pending');
      }
    }, (err) => {
      setLoadError(err?.message || 'Could not load delivery partners');
      setRiders([]);
    });
    return () => { unsubOrders(); unsubRiders(); };
  }, []);

  const earningsMap = useMemo(() => riderEarnings(orders, selectedDay), [orders, selectedDay]);

  const filtered = riders.filter(r => {
    const matchSearch = !search || r.name?.toLowerCase().includes(search.toLowerCase()) || r.phone?.includes(search) || r.vehicleNumber?.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === 'all' || (filter === 'approved' && r.isApproved) || (filter === 'blocked' && r.isBlocked) || (filter === 'pending' && !r.isApproved && !r.isBlocked);
    return matchSearch && matchFilter;
  });

  const handleApprove = async (rider) => {
    await updateRider(rider.id, { isApproved: true, isBlocked: false });
    toast.success(`${rider.name} approved ✓`);
  };

  const handleBlock = async (rider) => {
    await updateRider(rider.id, { isBlocked: true, isApproved: false });
    toast(`${rider.name} blocked`);
  };

  const handleDelete = async (rider) => {
    if (!window.confirm(`Delete ${rider.name}? This cannot be undone.`)) return;
    await deleteRider(rider.id);
    toast.success('Rider deleted');
    setSelected(null);
  };

  return (
    <div className="page-body">
      <div style={{ marginBottom: 24, display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16 }}>
        <div>
          <div style={{ fontFamily: 'Clash Display, sans-serif', fontSize: 24, fontWeight: 700, marginBottom: 4 }}>
            {FEATURES.RIDES_ENABLED ? 'Riders' : 'Delivery Partners'}
          </div>
          <div style={{ fontSize: 13, color: 'var(--text2)' }}>
            {riders.length} registered {FEATURES.RIDES_ENABLED ? 'riders' : 'partners'}
          </div>
        </div>
        {!FEATURES.RIDES_ENABLED && (
          <DateFilterBar selectedDay={selectedDay} onChange={setSelectedDay} />
        )}
      </div>

      {loadError && (
        <div style={{ padding: 14, marginBottom: 16, background: 'rgba(255,23,68,0.08)', border: '1px solid rgba(255,23,68,0.25)', borderRadius: 10, fontSize: 13, color: 'var(--red)' }}>
          {loadError}. Make sure you are signed in with an admin account (see Firestore <code>admin</code> collection).
        </div>
      )}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 300 }}>
          <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 14 }}>🔍</span>
          <input placeholder="Search by name, phone, vehicle..." value={search} onChange={e => setSearch(e.target.value)} style={{ paddingLeft: 32 }} />
        </div>
        {['all', 'approved', 'pending', 'blocked'].map(f => (
          <button key={f} className={`btn ${filter === f ? 'btn-green' : 'btn-ghost'} btn-sm`} onClick={() => setFilter(f)} style={{ textTransform: 'capitalize', padding: '8px 14px' }}>
            {f}
          </button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 340px' : '1fr', gap: 20 }}>
        <div className="table-card">
          <table>
            <thead>
              <tr>
                <th>{FEATURES.RIDES_ENABLED ? 'RIDER' : 'PARTNER'}</th>
                <th>PHONE</th>
                <th>VEHICLE</th>
                <th>PLATE</th>
                {!FEATURES.RIDES_ENABLED && <th>DELIVERIES</th>}
                {!FEATURES.RIDES_ENABLED && <th>FEES</th>}
                <th>JOINED</th>
                <th>STATUS</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={FEATURES.RIDES_ENABLED ? 7 : 9} style={{ textAlign: 'center', color: 'var(--text2)', padding: 32 }}>No {FEATURES.RIDES_ENABLED ? 'riders' : 'partners'} found</td></tr>
              ) : filtered.map(r => {
                const earn = earningsMap[r.id];
                return (
                <tr key={r.id} onClick={() => setSelected(r)} style={{ cursor: 'pointer', background: selected?.id === r.id ? 'rgba(0,230,118,0.04)' : '' }}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--green-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, border: '1px solid var(--green-mid)', flexShrink: 0 }}>👨</div>
                      <div style={{ fontWeight: 600 }}>{r.name || 'Unknown'}</div>
                    </div>
                  </td>
                  <td style={{ fontFamily: 'JetBrains Mono', fontSize: 12 }}>{r.phone}</td>
                  <td>{r.vehicle || '—'}</td>
                  <td><span style={{ fontFamily: 'JetBrains Mono', fontSize: 11, background: 'var(--surface)', padding: '3px 8px', borderRadius: 4 }}>{r.vehicleNumber || '—'}</span></td>
                  {!FEATURES.RIDES_ENABLED && (
                    <td style={{ fontFamily: 'JetBrains Mono' }}>{earn?.deliveries ?? 0}</td>
                  )}
                  {!FEATURES.RIDES_ENABLED && (
                    <td style={{ fontFamily: 'JetBrains Mono', color: 'var(--green)' }}>₹{earn?.fees ?? 0}</td>
                  )}
                  <td style={{ fontSize: 12, color: 'var(--text2)' }}>{r.createdAt ? new Date(r.createdAt).toLocaleDateString('en-IN') : '—'}</td>
                  <td>
                    <span className={`badge ${r.isBlocked ? 'badge-red' : r.isApproved ? 'badge-green' : 'badge-orange'}`}>
                      {r.isBlocked ? 'Blocked' : r.isApproved ? 'Approved' : 'Pending'}
                    </span>
                  </td>
                  <td onClick={e => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {!r.isApproved && !r.isBlocked && (
                        <button className="btn btn-green btn-sm" onClick={() => handleApprove(r)}>Approve</button>
                      )}
                      {r.isApproved && !r.isBlocked && (
                        <button className="btn btn-red btn-sm" onClick={() => handleBlock(r)}>Block</button>
                      )}
                      {r.isBlocked && (
                        <button className="btn btn-ghost btn-sm" onClick={() => handleApprove(r)}>Unblock</button>
                      )}
                    </div>
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>

        {selected && (
          <div className="table-card" style={{ padding: 20, height: 'fit-content' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ fontFamily: 'Clash Display, sans-serif', fontSize: 16, fontWeight: 600 }}>{partnerLabel} Detail</div>
              <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', color: 'var(--text2)', cursor: 'pointer', fontSize: 18 }}>✕</button>
            </div>

            <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'var(--green-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 30, margin: '0 auto 12px', border: '2px solid var(--green-mid)' }}>👨</div>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{selected.name}</div>
              <div style={{ fontSize: 13, color: 'var(--text2)', fontFamily: 'JetBrains Mono', marginTop: 4 }}>{selected.phone}</div>
              <span className={`badge ${selected.isBlocked ? 'badge-red' : selected.isApproved ? 'badge-green' : 'badge-orange'}`} style={{ marginTop: 8, display: 'inline-flex' }}>
                {selected.isBlocked ? 'Blocked' : selected.isApproved ? 'Approved' : 'Pending Approval'}
              </span>
            </div>

            {!FEATURES.RIDES_ENABLED && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
                <div style={{ padding: 10, background: 'var(--surface)', borderRadius: 8, textAlign: 'center' }}>
                  <div style={{ fontSize: 10, color: 'var(--text2)', fontFamily: 'JetBrains Mono' }}>DELIVERIES</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--green)' }}>{earningsMap[selected.id]?.deliveries ?? 0}</div>
                </div>
                <div style={{ padding: 10, background: 'var(--surface)', borderRadius: 8, textAlign: 'center' }}>
                  <div style={{ fontSize: 10, color: 'var(--text2)', fontFamily: 'JetBrains Mono' }}>FEE TOTAL</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--green)' }}>₹{earningsMap[selected.id]?.fees ?? 0}</div>
                </div>
              </div>
            )}

            {[
              { label: 'VEHICLE', value: selected.vehicle },
              { label: 'PLATE NUMBER', value: selected.vehicleNumber, mono: true },
              { label: FEATURES.RIDES_ENABLED ? 'RIDER ID' : 'PARTNER ID', value: selected.id.slice(0, 16) + '...', mono: true },
              { label: 'JOINED', value: selected.createdAt ? new Date(selected.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '—' },
            ].map(f => (
              <div key={f.label} style={{ marginBottom: 12, padding: 10, background: 'var(--surface)', borderRadius: 8 }}>
                <div style={{ fontSize: 10, color: 'var(--text2)', fontFamily: 'JetBrains Mono', marginBottom: 3, letterSpacing: 0.5 }}>{f.label}</div>
                <div style={{ fontSize: 13, fontWeight: 600, fontFamily: f.mono ? 'JetBrains Mono' : 'Epilogue', color: f.mono ? 'var(--green)' : 'var(--text)' }}>{f.value || '—'}</div>
              </div>
            ))}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
              {!selected.isApproved && !selected.isBlocked && <button className="btn btn-green" onClick={() => { handleApprove(selected); setSelected({ ...selected, isApproved: true }); }}>✓ Approve {partnerLabel}</button>}
              {selected.isApproved && !selected.isBlocked && <button className="btn btn-red" onClick={() => { handleBlock(selected); setSelected({ ...selected, isApproved: false, isBlocked: true }); }}>Block {partnerLabel}</button>}
              {selected.isBlocked && <button className="btn btn-ghost" onClick={() => { handleApprove(selected); setSelected({ ...selected, isApproved: true, isBlocked: false }); }}>Unblock {partnerLabel}</button>}
              <button className="btn btn-ghost" style={{ color: 'var(--red)', borderColor: 'rgba(255,23,68,0.2)' }} onClick={() => handleDelete(selected)}>🗑 Delete {partnerLabel}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
